import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';

function StatCard({ label, value, sub, color = 'blue', icon }) {
  const colors = {
    blue:   'bg-blue-50   border-blue-200   text-blue-700',
    amber:  'bg-amber-50  border-amber-200  text-amber-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    green:  'bg-green-50  border-green-200  text-green-700',
    red:    'bg-red-50    border-red-200    text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    slate:  'bg-slate-50  border-slate-200  text-slate-700',
  };
  return (
    <div className={`border rounded-xl p-5 ${colors[color] || colors.blue}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium opacity-75">{label}</div>
        {icon && <span className="text-lg opacity-60">{icon}</span>}
      </div>
      <div className="text-4xl font-bold mt-1 tracking-tight">{value}</div>
      {sub && <div className="text-xs mt-1.5 opacity-65">{sub}</div>}
    </div>
  );
}

function formatUsd(amount) {
  if (!amount) return '$0';
  return '$' + Number(amount).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function Dashboard() {
  const [orders, setOrders]       = useState([]);
  const [counts, setCounts]       = useState({});
  const [stats, setStats]         = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [error, setError]         = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  async function loadData() {
    try {
      const [ord, sync, st, cnts] = await Promise.all([
        api.getOrders({ status: 'awaiting_packaging' }),
        api.getSyncStatus(),
        api.getOrderStats(),
        api.getOrderCounts(),
      ]);
      setOrders(ord);
      setSyncStatus(sync);
      setStats(st);
      setCounts(cnts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function handleSync() {
    setSyncing(true);
    try {
      await api.syncOrders();
      await loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  }

  // Gecikmiş = shipment_date geçmiş, henüz paketlenmemiş
  const overdue = orders.filter(
    (o) => o.shipment_date && o.shipment_date.slice(0, 10) < today
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  }

  return (
    <div className="p-8">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {syncing ? <Spinner size="sm" /> : <span>🔄</span>}
          {syncing ? 'Sync ediliyor...' : 'Şimdi Sync Et'}
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Statü kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard
          icon="📦"
          label="Bekleyen"
          value={counts.awaiting_packaging ?? '—'}
          sub="Paketleme bekliyor"
          color="amber"
        />
        <StatCard
          icon="🚚"
          label="Teslim Bekliyor"
          value={counts.awaiting_deliver ?? '—'}
          sub="Kurye alımı bekleniyor"
          color="blue"
        />
        <StatCard
          icon="🛵"
          label="Kargoda"
          value={counts.delivering ?? '—'}
          sub="Dağıtımda"
          color="indigo"
        />
        <StatCard
          icon="⚠️"
          label="Gecikmiş"
          value={overdue.length}
          sub="Sevk tarihi geçmiş"
          color={overdue.length > 0 ? 'red' : 'green'}
        />
      </div>

      {/* Aylık istatistikler */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon="📊"
          label="Bu Ay Sipariş"
          value={stats?.month_orders ?? '—'}
          sub="İptal edilmeyenler"
          color="blue"
        />
        <StatCard
          icon="💵"
          label="Bu Ay Ciro"
          value={stats ? formatUsd(stats.month_revenue) : '—'}
          sub="Ürün toplam tutarı"
          color="green"
        />
        <StatCard
          icon="✅"
          label="Bu Ay Teslim"
          value={stats?.delivered_month ?? '—'}
          sub="Başarılı teslim"
          color="green"
        />
        <StatCard
          icon="🔴"
          label="Stokta Yok"
          value={stats?.out_of_stock ?? '—'}
          sub="Sıfır stoklu ürün"
          color={stats?.out_of_stock > 0 ? 'red' : 'slate'}
        />
      </div>

      {/* Sync durumu */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-8">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Sync Durumu</h2>
        {syncStatus?.lastSync ? (
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-green-500 inline-block flex-shrink-0"></span>
            <span className="text-sm text-gray-600">
              Son başarılı sync:{' '}
              <strong>{new Date(syncStatus.lastSync.timestamp).toLocaleString('tr-TR')}</strong>
              {' '}— {syncStatus.lastSync.synced} sipariş güncellendi
            </span>
          </div>
        ) : syncStatus?.lastSyncError ? (
          <div className="text-sm text-red-600">Hata: {syncStatus.lastSyncError}</div>
        ) : (
          <div className="text-sm text-gray-400">Henüz sync yapılmadı.</div>
        )}
      </div>

      {/* Yaklaşan sevkiyatlar */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Bekleyen Siparişler</h2>
          <Link to="/orders" className="text-sm text-blue-600 hover:underline">Tümünü gör →</Link>
        </div>
        <div className="divide-y divide-gray-50">
          {orders.slice(0, 8).map((order) => {
            const items = (order.items || []).filter(Boolean);
            const isLate = order.shipment_date && order.shipment_date.slice(0, 10) < today;
            return (
              <Link
                key={order.posting_number}
                to={`/orders/${order.posting_number}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-800 font-mono">{order.posting_number}</div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">
                    {items.map((i) => i.offer_id).join(', ').slice(0, 60) || '—'}
                  </div>
                </div>
                <div className="text-right ml-4 flex-shrink-0">
                  <div className={`text-xs font-medium ${isLate ? 'text-red-600' : 'text-gray-500'}`}>
                    {isLate && '⚠️ '}
                    {order.shipment_date
                      ? new Date(order.shipment_date).toLocaleDateString('tr-TR')
                      : '—'}
                  </div>
                </div>
              </Link>
            );
          })}
          {!orders.length && (
            <div className="px-5 py-8 text-center text-gray-400 text-sm">
              Bekleyen sipariş yok.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
